import { useMemo, useRef, useState, type KeyboardEvent } from "react";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useMenuNavMode } from "./use-menu-nav-mode";
import {
  buildAccountMenuModel,
  normalizeQuery,
  DEFAULT_UNGROUPED_LABEL,
  shouldShowSearch,
} from "./account-menu-model";
import {
  setGroupCollapsed,
  toggleGroupCollapsed,
  useCollapsedGroups,
} from "./group-collapse-store";
import {
  AccountMenuItem,
  GroupHeader,
  MenuEmptyState,
  MenuSearch,
} from "./menu-parts";
import type {
  AccountMenuChoice,
  SelectorAccount,
  SelectorGroup,
  SelectorSize,
} from "./types";

export interface AccountMenuProps {
  accounts: SelectorAccount[];
  groups?: SelectorGroup[];
  value?: AccountMenuChoice;
  onChange: (choice: AccountMenuChoice) => void;
  /** Undefined lets the list decide: a search box below six accounts is noise. */
  searchable?: boolean;
  includeAll?: boolean;
  selectableGroups?: boolean;
  allLabel?: string;
  ungroupedLabel?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  size?: SelectorSize;
  className?: string;
  /** Seeds the query. Stories and tests, not product code. */
  initialQuery?: string;
}

export function AccountMenu({
  accounts,
  groups = [],
  value,
  onChange,
  searchable,
  includeAll = false,
  selectableGroups = false,
  allLabel = "All accounts",
  ungroupedLabel = DEFAULT_UNGROUPED_LABEL,
  searchPlaceholder = "Search accounts...",
  emptyMessage = "No accounts found.",
  size = "md",
  className,
  initialQuery = "",
}: AccountMenuProps) {
  const [query, setQuery] = useState(initialQuery);
  const listRef = useRef<HTMLDivElement>(null);
  const collapsedGroupIds = useCollapsedGroups();
  const model = useMemo(
    () =>
      buildAccountMenuModel({
        accounts,
        groups,
        query,
        collapsedGroupIds,
        ungroupedLabel,
        namedGroupHeaders: selectableGroups,
      }),
    [
      accounts,
      groups,
      query,
      collapsedGroupIds,
      ungroupedLabel,
      selectableGroups,
    ]
  );

  const showSearch = shouldShowSearch(searchable, model.totalAccounts);

  // This row sits outside the grouped model, and cmdk's own filtering is off,
  // so it has to answer the query itself.
  const allRowMatches =
    !model.hasQuery || normalizeQuery(allLabel).includes(normalizeQuery(query));

  const { navMode, navHandlers } = useMenuNavMode();
  // Controlled so collapsing can hand the highlight to the header. The
  // `onValueChange` is required: a controlled cmdk value without one freezes
  // the highlight and the arrow keys stop working.
  const [commandValue, setCommandValue] = useState<string | undefined>(
    undefined
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    navHandlers.onKeyDown(event);
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    // A caret that can move wins over collapse.
    const target = event.target as HTMLElement | null;
    if (target instanceof HTMLInputElement && target.value.length > 0) return;
    if (model.hasQuery) return;

    const highlighted = listRef.current?.querySelector<HTMLElement>(
      '[data-section-id][data-selected="true"]'
    );
    const sectionId = highlighted?.dataset.sectionId;
    const section = model.sections.find((entry) => entry.id === sectionId);
    if (!sectionId || !section?.showHeader) return;
    event.preventDefault();
    const collapsing = event.key === "ArrowLeft";
    setGroupCollapsed(sectionId, collapsing);
    // Collapsing removes the row the highlight is on, so it moves to the
    // header or ArrowRight has nothing to reopen.
    if (collapsing) setCommandValue(`__group__ ${sectionId}`);
  };

  return (
    <Command
      shouldFilter={false}
      value={commandValue}
      onValueChange={setCommandValue}
      data-selector-menu=""
      // Without this cmdk announces its own default name for the list.
      label="Accounts"
      onKeyDown={handleKeyDown}
      onPointerMove={navHandlers.onPointerMove}
      data-nav={navMode}
      className={cn(
        // `group/menu` publishes the nav mode to the rows; `overflow-hidden`
        // stops square row fills escaping the rounded corners.
        "group/menu overflow-hidden",
        "h-auto max-h-[min(60vh,var(--radix-popover-content-available-height,60vh))] rounded-lg border-neutral-100 py-1 shadow-[0_4px_8px_-2px_rgba(16,24,40,0.10),0_2px_4px_-2px_rgba(16,24,40,0.06)]",
        className
      )}
    >
      {showSearch ? (
        <MenuSearch
          value={query}
          onValueChange={setQuery}
          placeholder={searchPlaceholder}
          size={size}
        />
      ) : null}
      <CommandList
        ref={listRef}
        focusable={!showSearch}
        // `scroll-pt` because cmdk scrolls with `block: "nearest"`, which is
        // blind to the sticky headers. `max-h-none` because the list primitive
        // ships a 300px cap that silently overrides the shell's own height.
        className="min-h-0 max-h-none flex-1 scroll-pt-11"
      >
        {includeAll && allRowMatches ? (
          <CommandGroup className="overflow-visible p-0">
            <CommandItem
              value="__all__"
              data-chosen={value?.kind === "all" || undefined}
              onSelect={() => onChange({ kind: "all" })}
              className={cn(
                "w-full cursor-pointer gap-2 rounded-none font-inter text-sm font-medium text-neutral-800",
                "h-11",
                size === "md" ? "px-3.5" : "px-3",
                "data-[chosen]:bg-blue-50 data-[chosen]:hover:bg-blue-50 data-[chosen]:data-[selected=true]:bg-blue-50",
                "hover:bg-neutral-50 data-[selected=true]:bg-neutral-50 group-data-[nav=keyboard]/menu:data-[selected=true]:ring-1 group-data-[nav=keyboard]/menu:data-[selected=true]:ring-inset group-data-[nav=keyboard]/menu:data-[selected=true]:ring-blue-500"
              )}
            >
              <span className="min-w-0 flex-1 truncate">{allLabel}</span>
              {value?.kind === "all" ? (
                <>
                  <span className="sr-only">Selected</span>
                  <i
                    aria-hidden
                    className="fa-regular fa-check ml-auto size-5 text-center text-[16px] leading-5 text-blue-500"
                  />
                </>
              ) : null}
            </CommandItem>
          </CommandGroup>
        ) : null}
        {model.visibleAccounts.length === 0 &&
        !(includeAll && allRowMatches) ? (
          <MenuEmptyState
            query={query}
            message={emptyMessage}
            onClear={() => setQuery("")}
          />
        ) : null}
        {model.sections.map((section, index) => {
          const { showHeader } = section;
          // A group with nothing behind it is still a heading.
          const selectableGroupRow =
            selectableGroups &&
            !section.isUngrouped &&
            section.group?.selectable !== false;
          // Collapsed, a caption still has to be an option: its rows are gone,
          // so nothing else can hold the highlight ArrowRight reopens it from.
          const interactive = selectableGroupRow || section.collapsed;
          return (
            <CommandGroup
              key={section.id}
              // CommandGroup ships `overflow-hidden`, which clips a sticky
              // header out of existence.
              className={cn(
                "overflow-visible p-0",
                (index > 0 || includeAll) && "border-t border-neutral-100"
              )}
            >
              {showHeader ? (
                <GroupHeader
                  label={section.label}
                  sectionId={section.id}
                  count={section.matchedCount}
                  collapsed={section.collapsed}
                  query={query}
                  size={size}
                  interactive={interactive}
                  accounts={section.group?.accounts}
                  value={`__group__ ${section.id}`}
                  disabled={section.group?.disabled}
                  disabledReason={section.group?.disabledReason}
                  caption={section.group?.caption}
                  selected={
                    selectableGroupRow &&
                    value?.kind === "group" &&
                    value.id === section.id
                  }
                  onSelect={() =>
                    selectableGroupRow
                      ? onChange({ kind: "group", id: section.id })
                      : toggleGroupCollapsed(section.id)
                  }
                  onToggleCollapse={
                    model.hasQuery
                      ? undefined
                      : () => toggleGroupCollapsed(section.id)
                  }
                />
              ) : null}
              {section.accounts.map((account) => (
                <AccountMenuItem
                  key={account.id}
                  account={account}
                  size={size}
                  query={query}
                  sectionId={section.id}
                  nested={showHeader}
                  selected={
                    value?.kind === "account" && value.id === account.id
                  }
                  onSelect={() => onChange({ kind: "account", id: account.id })}
                />
              ))}
            </CommandGroup>
          );
        })}
      </CommandList>
    </Command>
  );
}
